import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  getLoginPasswordCheckKey,
  getPasswordCheckDebounceMs,
} from './loginIdentifier';

/**
 * Debounced /users/check-password-set for login identifiers.
 * Email waits longer than phone so TLD typing (e.g. .com) does not spam the API.
 */
export function usePasswordSetCheck(phone, email) {
  const checkKey = getLoginPasswordCheckKey(phone, email);
  const [hasPasswordSet, setHasPasswordSet] = useState(null);
  const [checkingPasswordSet, setCheckingPasswordSet] = useState(false);
  const [settledCheckKey, setSettledCheckKey] = useState(null);

  const isEmailCheck = Boolean(checkKey?.startsWith('email:'));
  const debounceMs = getPasswordCheckDebounceMs({ isEmail: isEmailCheck });

  const isPasswordCheckReady =
    checkKey !== null && settledCheckKey === checkKey && hasPasswordSet !== null;
  const isPasswordCheckPending =
    checkKey !== null && (checkingPasswordSet || settledCheckKey !== checkKey);

  useEffect(() => {
    if (!checkKey) {
      setHasPasswordSet(null);
      setSettledCheckKey(null);
      setCheckingPasswordSet(false);
      return undefined;
    }

    let cancelled = false;
    const t = setTimeout(async () => {
      if (cancelled) return;
      setCheckingPasswordSet(true);
      try {
        const payload = checkKey.startsWith('phone:')
          ? { phone: checkKey.slice(6) }
          : { email: checkKey.slice(6) };
        const res = await axios.post('/users/check-password-set', payload);
        if (!cancelled && res?.data?.data?.hasPassword !== undefined) {
          setHasPasswordSet(res.data.data.hasPassword);
          setSettledCheckKey(checkKey);
        }
      } catch {
        if (!cancelled) {
          setHasPasswordSet(null);
          setSettledCheckKey(null);
        }
      } finally {
        if (!cancelled) setCheckingPasswordSet(false);
      }
    }, debounceMs);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [checkKey, debounceMs]);

  return {
    checkKey,
    hasPasswordSet,
    checkingPasswordSet,
    settledCheckKey,
    isPasswordCheckReady,
    isPasswordCheckPending,
    usesPhoneIdentifier: checkKey?.startsWith('phone:') ?? false,
  };
}
